import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LEGAL_VERSION } from '../../shared/legalVersion';
import {
  JOB_OPTIONS,
  INDUSTRY_OPTIONS,
  isJobOption,
  isIndustryOption,
  profileNeedsCompletion,
} from '../../shared/types';
import { updateProfile } from '../services/api';

// Bloque l'accès à l'application tant que le métier ou le secteur d'activité
// manque au profil. Couvre les comptes qui n'ont pas vu le formulaire
// d'inscription par email :
//   1. inscription par Google ou par code reçu par email ;
//   2. comptes créés avant que ces deux champs deviennent obligatoires.
// Calqué sur TermsGate, et affiché APRÈS lui : une seule fenêtre à la fois.

const SELECT_CLASS =
  'w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-violet-500 [&>option]:text-slate-900 [&>option]:bg-white dark:[&>option]:text-white dark:[&>option]:bg-slate-800';

export default function ProfileGate() {
  const { user, refreshProfile } = useAuth();
  const [jobTitle, setJobTitle] = useState('');
  const [industry, setIndustry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;
  // TermsGate passe d'abord.
  if (!user.termsAcceptedAt || user.termsVersion !== LEGAL_VERSION) return null;
  if (!profileNeedsCompletion(user)) return null;

  // Un des deux champs peut déjà être rempli (ancienne valeur libre comprise) :
  // on ne demande que ce qui manque.
  const askJob = !user.jobTitle?.trim();
  const askIndustry = !user.industry?.trim();
  const ready = (!askJob || isJobOption(jobTitle)) && (!askIndustry || isIndustryOption(industry));

  const handleSave = async () => {
    if (!ready) {
      setError("Choisissez votre métier et votre secteur d'activité.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await updateProfile({
        ...(askJob ? { jobTitle } : {}),
        ...(askIndustry ? { industry } : {}),
      });
      await refreshProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-violet-600 dark:text-violet-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
              Complétez votre profil
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Deux questions avant de continuer. Elles nous aident à adapter Evinly à votre activité.
            </p>
          </div>
        </div>

        {askJob && (
          <div>
            <label htmlFor="profile-gate-job" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Votre métier
            </label>
            <select
              id="profile-gate-job"
              value={jobTitle}
              onChange={e => setJobTitle(e.target.value)}
              disabled={loading}
              className={`${SELECT_CLASS} ${jobTitle ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}
            >
              <option value="" disabled>Choisissez votre métier</option>
              {JOB_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}

        {askIndustry && (
          <div>
            <label htmlFor="profile-gate-industry" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Votre secteur d'activité
            </label>
            <select
              id="profile-gate-industry"
              value={industry}
              onChange={e => setIndustry(e.target.value)}
              disabled={loading}
              className={`${SELECT_CLASS} ${industry ? 'text-slate-900 dark:text-white' : 'text-slate-400'}`}
            >
              <option value="" disabled>Choisissez votre secteur</option>
              {INDUSTRY_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        <button
          onClick={handleSave}
          disabled={loading || !ready}
          className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          {loading ? 'Enregistrement...' : 'Continuer'}
        </button>
      </div>
    </div>
  );
}
